import { useState } from 'react';
import { CopyRuleModal } from '../components/copy-rule/copy-rule-modal';
import { DeleteRuleModal } from '../components/delete-rule-modal';
import { RunRuleModal } from '../components/run-rule/run-rule-modal';

export type RuleAction = 'run' | 'copy' | 'delete';

export interface RuleActionTarget {
  ruleId: string;
  ruleName: string;
}

interface UseRuleActionModalsParams {
  deleteRule: ({ variables }: { variables: { id: string } }) => void;
  deleteRuleLoading: boolean;
  copyRuleLoading: boolean;
  runRuleLoading: boolean;
  refetchListRules: () => void;
}

interface UseRuleActionModals {
  openModal: (action: RuleAction, target: RuleActionTarget) => void;
  modals: JSX.Element;
}

// One set of Run / Copy / Delete modals per rules grid. Render `modals` next to the
// DataGrid, never inside a cell: the v6 grid publishes cell events for anything that
// bubbles out of a cell's React subtree, including portaled dialogs, so a modal
// rendered inside a cell has its keystrokes handled by the grid as well.
export const useRuleActionModals = ({
  deleteRule,
  deleteRuleLoading,
  copyRuleLoading,
  runRuleLoading,
  refetchListRules,
}: UseRuleActionModalsParams): UseRuleActionModals => {
  const [openAction, setOpenAction] = useState<RuleAction | null>(null);
  const [target, setTarget] = useState<RuleActionTarget>({
    ruleId: '',
    ruleName: '',
  });

  const openModal = (
    action: RuleAction,
    nextTarget: RuleActionTarget,
  ): void => {
    setTarget(nextTarget);
    setOpenAction(action);
  };

  const handleClose = (): void => setOpenAction(null);

  // Mount nothing until the first action is clicked; afterwards keep the last target
  // so the closing transition still has the rule to show.
  const modals = target.ruleId ? (
    <>
      <RunRuleModal
        ruleId={target.ruleId}
        isOpen={openAction === 'run'}
        handleClose={handleClose}
        runRuleLoading={runRuleLoading}
      />
      <CopyRuleModal
        ruleId={target.ruleId}
        isOpen={openAction === 'copy'}
        handleClose={handleClose}
        refetchListRules={refetchListRules}
        copyRuleLoading={copyRuleLoading}
      />
      <DeleteRuleModal
        ruleId={target.ruleId}
        ruleName={target.ruleName}
        isOpen={openAction === 'delete'}
        handleClose={handleClose}
        deleteRule={deleteRule}
        deleteRuleLoading={deleteRuleLoading}
      />
    </>
  ) : (
    <></>
  );

  return { openModal, modals };
};
